/**
 * Login Page
 * Página de login para administradores
 * Validação: padrão vover-app — erros por campo (helperText + error)
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  CircularProgress
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useMessage } from '@/contexts/MessageContext';
import { getFriendlyErrorMessage } from '@/utils/getFriendlyErrorMessage';

type LoginFieldErrors = {
  email: string;
  password: string;
};

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [code, setCode] = useState('');
  const [fieldErrors, setFieldErrors] = useState<LoginFieldErrors>({ email: '', password: '' });
  const [codeError, setCodeError] = useState('');

  const { login, verify2FA, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { showError } = useMessage();

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      const from = (location.state as { from?: { pathname?: string } })?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, location]);

  const validateLoginForm = (): boolean => {
    const next: LoginFieldErrors = { email: '', password: '' };
    let ok = true;
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      next.email = 'Email é obrigatório';
      ok = false;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        next.email = 'Digite um email válido';
        ok = false;
      }
    }
    if (!password.trim()) {
      next.password = 'Senha é obrigatória';
      ok = false;
    }
    setFieldErrors(next);
    return ok;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateLoginForm()) return;

    setLoading(true);
    setFieldErrors({ email: '', password: '' });

    try {
      const result = await login(email, password);

      if (result.requires2FA) {
        setRequires2FA(true);
        setTempToken(result.tempToken!);
        setLoading(false);
      } else {
        await new Promise(resolve => setTimeout(resolve, 100));
        const from = (location.state as { from?: { pathname?: string } })?.from?.pathname || '/';
        navigate(from, { replace: true });
      }
    } catch (err: unknown) {
      const msg = getFriendlyErrorMessage(
        err,
        'Erro ao fazer login. Verifique sua conexão e tente novamente.'
      );
      setFieldErrors({ email: '', password: msg });
      showError(msg);
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setCodeError('');

    if (!code || code.length !== 6) {
      setCodeError('Informe o código de 6 dígitos');
      return;
    }

    setLoading(true);

    try {
      await verify2FA(tempToken, code, 'email');
      await new Promise(resolve => setTimeout(resolve, 100));
      const from = (location.state as { from?: { pathname?: string } })?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        'Código 2FA inválido ou expirado';
      setCodeError(msg);
      showError(msg);
      setCode('');
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f7f8fc 0%, #e2e8f0 50%, #d1d8e4 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CircularProgress sx={{ color: '#21316a' }} />
      </Box>
    );
  }

  if (requires2FA) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f7f8fc 0%, #e2e8f0 50%, #d1d8e4 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2
        }}
      >
        <Container maxWidth="sm">
          <Paper elevation={10} sx={{ padding: 4, borderRadius: 3 }}>
            <Typography variant="h5" gutterBottom align="center">
              Verificação 2FA
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
              Digite o código de verificação enviado por email
            </Typography>

            <Box component="form" onSubmit={handleVerify2FA}>
              <TextField
                fullWidth
                label="Código de Verificação"
                value={code}
                onChange={e => {
                  setCode(e.target.value);
                  if (codeError) setCodeError('');
                }}
                margin="normal"
                required
                autoFocus
                inputProps={{ maxLength: 6 }}
                error={!!codeError}
                helperText={codeError || '6 dígitos enviados por email'}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 3,
                  mb: 2,
                  backgroundColor: '#21316a',
                  '&:hover': { backgroundColor: '#1a2854' }
                }}
                disabled={loading || code.length < 6}
              >
                {loading ? <CircularProgress size={24} /> : 'Verificar'}
              </Button>

              <Button
                fullWidth
                variant="text"
                onClick={() => {
                  setRequires2FA(false);
                  setCode('');
                  setCodeError('');
                }}
              >
                Voltar
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f7f8fc 0%, #d1d8e4 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={10} sx={{ padding: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <img
              src="/images/logo_vover.png"
              alt="Vover Admin"
              style={{ maxWidth: '180px', height: 'auto', display: 'block' }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Painel Administrativo
          </Typography>

          <Box component="form" onSubmit={handleLogin}>
            <TextField
              fullWidth
              label="E-mail"
              type="email"
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                if (fieldErrors.email) setFieldErrors(f => ({ ...f, email: '' }));
              }}
              margin="normal"
              required
              autoFocus
              error={!!fieldErrors.email}
              helperText={fieldErrors.email || 'Digite seu email administrativo'}
            />

            <TextField
              fullWidth
              label="Senha"
              type="password"
              value={password}
              onChange={e => {
                setPassword(e.target.value);
                if (fieldErrors.password) setFieldErrors(f => ({ ...f, password: '' }));
              }}
              margin="normal"
              required
              error={!!fieldErrors.password}
              helperText={fieldErrors.password || 'Sua senha de acesso'}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 3,
                mb: 2,
                backgroundColor: '#21316a',
                '&:hover': { backgroundColor: '#1a2854' }
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Entrar'}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage;
